import { ElementRef, Injectable, QueryList } from '@angular/core';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { IBulkItemId } from '@nimbus-admin/infrastructure/bulk-process/contracts/i-bulk-item-id';

@Injectable()
export class BulkProcessService<TId> {
  private readonly boundaryElement: HTMLElement = document.querySelector('body');
  private bulkOverlayOffsetX = 50;
  private bulkItems$: BehaviorSubject<IBulkItemId<TId>[]> = new BehaviorSubject([]);
  private bulkItemsSelectedIds$ = new BehaviorSubject<TId[]>([]);

  public bulkOverlayOrigin$ = new BehaviorSubject<ElementRef>(null);
  public bulkOverlayPositions$ = new BehaviorSubject<ConnectedPosition[]>([
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: this.bulkOverlayOffsetX, offsetY: -16 },
  ]);
  public isBulkOverlayOpen$ = new BehaviorSubject(false);
  public areAllBulkItemsSelected$ = new BehaviorSubject(false);

  public initialize(getBulkItemsAllCheckBox: () => ElementRef, getBulkItemCheckBoxes: () => QueryList<ElementRef>): void {
    this.getBulkItemsAllCheckBox = getBulkItemsAllCheckBox;
    this.getBulkItemCheckBoxes = getBulkItemCheckBoxes;
  }

  public initializeBulkItems(items: IBulkItemId<TId>[]): void {
    const bulkItemsSelectedIdsOld = this.bulkItemsSelectedIds$.value;
    const bulkItemsSelectedIdsNew = items
      .filter((x) => bulkItemsSelectedIdsOld.some((id) => isEqual(id, x.bulkItemId)))
      .map((x) => x.bulkItemId);
    this.clearBulkProcess();
    this.bulkItems$.next(items);
    this.updateBulkItemsSelectedIds$(bulkItemsSelectedIdsNew);
  }

  public getBulkItems$(): Observable<IBulkItemId<TId>[]> {
    return this.bulkItems$.asObservable();
  }

  public getBulkItemsSelectedIds$(): Observable<TId[]> {
    return this.bulkItemsSelectedIds$.asObservable();
  }

  public getBulkItemsSelectedIds(): TId[] {
    return this.bulkItemsSelectedIds$.value;
  }

  public isBulkItemSelected(dataItem: IBulkItemId<TId>): boolean {
    const bulkItemsSelectedIds = this.bulkItemsSelectedIds$.value;
    return bulkItemsSelectedIds.some((id) => isEqual(id, dataItem.bulkItemId));
  }

  public changeAllBulkItemsSelection(isChecked: boolean): void {
    this.areAllBulkItemsSelected$.next(isChecked);
    let selectedItems = [];
    if (isChecked) {
      selectedItems = this.bulkItems$.value.map((x) => x.bulkItemId);
    }
    this.updateBulkItemsSelectedIds$(selectedItems);
    if (isChecked && this.hasBulkItemsSelected) {
      this.openBulkOverlay(this.getBulkItemsAllCheckBox());
    }
  }

  public changedBulkItemSelection(isChecked: boolean, item: IBulkItemId<TId>, rowIndex: number): void {
    const bulkItemsSelectedIds = this.bulkItemsSelectedIds$.value;
    if (isChecked) {
      bulkItemsSelectedIds.push(item.bulkItemId);
    } else {
      this.areAllBulkItemsSelected$.next(false);
      const itemIndex = bulkItemsSelectedIds.findIndex((id) => isEqual(id, item.bulkItemId));
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (itemIndex > -1) {
        bulkItemsSelectedIds.splice(itemIndex, 1);
      }
    }
    this.updateBulkItemsSelectedIds$(bulkItemsSelectedIds);
    if (bulkItemsSelectedIds.length > 0) {
      const target = this.getBulkItemCheckBoxes().get(rowIndex);
      this.openBulkOverlay(target);
    } else {
      this.closeBulkOverlay();
    }
  }

  public onBulkOverlayDragEnded(event: CdkDragEnd): void {
    const getConstrainedPosition = (position: number): number => {
      const boundaryRect = this.boundaryElement.getBoundingClientRect();
      const popupElement = document.querySelector('.bulk-edit-popup');
      const minX = boundaryRect.left;
      const maxX = boundaryRect.right - popupElement.getBoundingClientRect().width;
      return Math.min(Math.max(position, minX), maxX);
    };

    const draggedElement = event.source.element.nativeElement;
    const finalX = draggedElement.getBoundingClientRect().left;
    const targetElement = this.bulkOverlayOrigin$.value.nativeElement;
    const targetOffset = targetElement.getBoundingClientRect().right;
    this.bulkOverlayOffsetX = getConstrainedPosition(finalX) - targetOffset;
  }

  public closeBulkOverlay(): void {
    this.isBulkOverlayOpen$.next(false);
  }

  public clearBulkProcess(): void {
    this.updateBulkItemsSelectedIds$([]);
    this.closeBulkOverlay();
  }

  private get hasBulkItemsSelected(): boolean {
    return this.bulkItemsSelectedIds$.value?.length > 0;
  }

  private getBulkItemsAllCheckBox: () => ElementRef = () => undefined;
  private getBulkItemCheckBoxes: () => QueryList<ElementRef> = () => undefined;

  private openBulkOverlay(target: ElementRef): void {
    if (!this.isBulkOverlayOpen$.value) {
      this.bulkOverlayPositions$.next([{ ...this.bulkOverlayPositions$.value[0], offsetX: this.bulkOverlayOffsetX }]);
    }
    setTimeout(() => {
      this.bulkOverlayOrigin$.next(target);
      this.isBulkOverlayOpen$.next(true);
    });
  }

  private updateBulkItemsSelectedIds$(bulkItemsSelectedIds: TId[]): void {
    this.bulkItemsSelectedIds$.next(bulkItemsSelectedIds);
    if (bulkItemsSelectedIds.length === this.bulkItems$.value.length) {
      this.areAllBulkItemsSelected$.next(true);
    }
    if (bulkItemsSelectedIds.length === 0) {
      this.areAllBulkItemsSelected$.next(false);
      this.closeBulkOverlay();
    }
  }
}
